<template>
  <v-card
    variant="outlined"
    class="action-item-card mb-3"
    :data-finding-id="item.id"
    :class="{
      'brand-paywall-blur': blurred,
      'action-item-card-clickable': !blurred,
      'action-item-card-returned': highlighted
    }"
    :role="blurred ? undefined : 'link'"
    :tabindex="blurred ? undefined : 0"
    @click="onCardClick"
    @keydown.enter.prevent="onCardClick"
  >
    <v-card-text class="pa-4">
      <div class="pr-2 action-item-main">
        <div class="d-flex align-start justify-space-between flex-wrap ga-2 mb-2">
          <div class="text-caption text-medium-emphasis">
            {{ idx + 1 }} — {{ item.code || 'Action item' }}
          </div>
          <div class="d-flex flex-wrap ga-1">
            <v-chip size="x-small" :color="severityColor(item.severity)" variant="tonal">
              {{ normalizeLabel(item.severity) }}
            </v-chip>
            <v-chip size="x-small" variant="outlined">
              {{ normalizeLabel(item.fix_effort) }} effort
            </v-chip>
          </div>
        </div>
        <div class="text-subtitle-1 font-weight-bold mb-2">{{ item.title }}</div>
        <div class="d-flex align-center flex-wrap ga-1 mb-2">
          <span class="gap-types-label">Gap types:</span>
          <v-chip
            v-for="gapType in gapTypeChips(item)"
            :key="`${item.key}-gap-${gapType}`"
            size="x-small"
            variant="outlined"
          >
            {{ formatGapTypeLabel(gapType) }}
          </v-chip>
        </div>
        <div class="text-body-2 text-medium-emphasis mb-2">{{ item.description }}</div>
        <div class="d-flex align-center justify-space-between flex-wrap ga-2">
          <div class="affected-queries-wrap">
            <button
              type="button"
              class="affected-toggle-btn"
              :disabled="blurred"
              @click.stop="onToggleAffected"
            >
              <span class="affected-toggle-text">
                Affects {{ affectedQueries.length }}/{{ totalQueryCount }} queries
              </span>
              <v-icon size="16" class="affected-toggle-icon">
                {{ affectedExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </button>
            <div v-if="affectedExpanded && !blurred" class="affected-query-list">
              <div
                v-for="query in affectedQueries"
                :key="`${item.key}-affected-${query.id}`"
                class="affected-query-item"
              >
                <v-icon size="14" class="mr-2 text-medium-emphasis">mdi-magnify</v-icon>
                <span>{{ query.text || `Query ${query.id}` }}</span>
              </div>
            </div>
          </div>
          <span v-if="!blurred" class="view-details-link">View details</span>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  formatGapTypeLabel,
  gapTypeChips,
  normalizeLabel,
  severityColor
} from '@/utils/diagnosisFindings'

export default {
  name: 'ActionCenterFindingCard',
  props: {
    item: {
      type: Object,
      required: true
    },
    idx: {
      type: Number,
      required: true
    },
    blurred: {
      type: Boolean,
      default: false
    },
    totalQueryCount: {
      type: Number,
      default: 0
    },
    affectedQueries: {
      type: Array,
      default: () => []
    },
    affectedExpanded: {
      type: Boolean,
      default: false
    },
    highlighted: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-affected', 'view-details'],
  methods: {
    formatGapTypeLabel,
    gapTypeChips,
    normalizeLabel,
    severityColor,
    onCardClick() {
      if (this.blurred) return
      this.$emit('view-details')
    },
    onToggleAffected() {
      if (this.blurred) return
      this.$emit('toggle-affected')
    }
  }
}
</script>

<style scoped>
.action-item-card {
  border-radius: 14px;
  border-color: var(--brand-sand-300) !important;
}

.action-item-card-clickable {
  cursor: pointer;
}

.action-item-card-clickable:hover {
  border-color: var(--brand-ocean-600) !important;
}

.action-item-card-returned {
  border-color: var(--brand-ocean-600) !important;
  box-shadow: 0 0 0 3px rgba(13, 92, 143, 0.18);
}

.action-item-main {
  flex: 1 1 auto;
  min-width: 260px;
}

.gap-types-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--brand-ink-700);
  margin-right: 2px;
}

.affected-queries-wrap {
  margin-top: 8px;
}

.affected-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--brand-sand-300);
  border-radius: 999px;
  padding: 4px 10px;
  background: #fff;
  color: var(--brand-ocean-600);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.affected-query-list {
  margin-top: 8px;
  border: 1px solid var(--brand-sand-300);
  border-radius: 10px;
  background: var(--brand-sand-50);
  padding: 8px;
}

.affected-query-item {
  display: flex;
  align-items: flex-start;
  padding: 5px 4px;
  font-size: 12px;
  color: var(--brand-ink-800);
  line-height: 1.4;
}

.view-details-link {
  font-size: 12px;
  font-weight: 700;
  color: var(--brand-ocean-600);
  white-space: nowrap;
}
</style>
